/* Drawer-only presentation: native Shopify cart mutations remain in cart.js. */
if (!customElements.get('levee-cart-drawer-ui')) {
  class LeveeCartDrawerUI extends HTMLElement {
    connectedCallback() {
      this.cart = this.closest('cart-drawer');
      this.drawer = this.closest('drawer-component');
      this.scroller = this.cart?.querySelector('.f-drawer__scroller');
      if (!this.scroller || !this.drawer) return;
      this.rememberOutsideFocus = (event) => {
        const target = event.target;
        if (target !== document.body && target !== document.documentElement && !this.drawer.contains(target) && typeof target.focus === 'function') this.outsideFocus = target;
      };
      this.rememberOutsideFocus({ target: document.activeElement });
      this.onClose = () => {
        const canFocus = (target) => target?.isConnected && !target.disabled && target.getClientRects().length > 0 && getComputedStyle(target).visibility !== 'hidden' && !target.closest('[inert]');
        const opener = Array.from(document.querySelectorAll('drawer-opener')).find((element) => element.dataset.drawer === '#' + this.drawer.id)?.querySelector('button, [data-opener]');
        const target = [this.drawer.openedBy, this.outsideFocus, opener].find(canFocus) || null;
        // Overlay closes skip native focus cleanup; every close must release the trap.
        if (typeof removeTrapFocus === 'function') removeTrapFocus(target);
        else target?.focus({ preventScroll: true });
      };
      document.addEventListener('focusin', this.rememberOutsideFocus);
      this.drawer.addEventListener('close', this.onClose);
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.updateOverflow());
      };
      this.onClick = (event) => {
        const close = event.target.closest('[data-cart-continue]');
        if (close && this.cart.contains(close)) {
          event.preventDefault();
          this.drawer.closeDrawer();
          return;
        }
        const cue = event.target.closest('[data-cart-scroll-cue]');
        if (cue && this.cart.contains(cue)) {
          event.preventDefault();
          this.scroller.scrollBy({
            top: Math.max(100, this.scroller.clientHeight * 0.65),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
          });
        }
      };
      this.cart.addEventListener('click', this.onClick);
      this.scroller.addEventListener('scroll', this.schedule, { passive: true });
      this.drawer.addEventListener('open', this.schedule);
      this.cart.addEventListener('load', this.schedule, true);
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this.scroller);
        this.resizeObserver.observe(this.cart);
        this.observeItems();
      }
      this.mutationObserver = new MutationObserver(() => {
        this.observeItems();
        this.schedule();
      });
      this.mutationObserver.observe(this.cart, { childList: true, subtree: true });
      window.visualViewport?.addEventListener('resize', this.schedule);
      this.schedule();
    }

    observeItems() {
      const items = this.cart.querySelector('[data-cart-items]');
      if (items === this.items) return;
      if (this.items) this.resizeObserver?.unobserve(this.items);
      this.items = items;
      if (items) this.resizeObserver?.observe(items);
    }

    updateOverflow() {
      if (!this.isConnected) return;
      const remaining = this.scroller.scrollHeight - this.scroller.clientHeight - this.scroller.scrollTop;
      const hasMore = this.scroller.clientHeight > 0 && remaining > 8;
      this.cart.classList.toggle('has-more-cart-content', hasMore);
      const cue = this.cart.querySelector('[data-cart-scroll-cue]');
      if (cue) cue.hidden = !hasMore;
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.cart?.removeEventListener('click', this.onClick);
      this.scroller?.removeEventListener('scroll', this.schedule);
      this.drawer?.removeEventListener('open', this.schedule);
      this.drawer?.removeEventListener('close', this.onClose);
      document.removeEventListener('focusin', this.rememberOutsideFocus);
      this.cart?.removeEventListener('load', this.schedule, true);
      window.visualViewport?.removeEventListener('resize', this.schedule);
    }
  }
  customElements.define('levee-cart-drawer-ui', LeveeCartDrawerUI);
}
